import { supabase } from '@/lib/supabase';

export interface Subscription {
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'incomplete' | 'unpaid';
  planId: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  trialEnd: string | null;
}

const FUNCTIONS_BASE = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`;

let billingEnabledCache: boolean | null = null;
let subCache: { userId: string; sub: Subscription | null } | null = null;

/** Is the global billing switch on? Cached for the session. */
export async function fetchBillingEnabled(force = false): Promise<boolean> {
  if (!force && billingEnabledCache !== null) return billingEnabledCache;

  const { data, error } = await supabase
    .from('app_settings')
    .select('value')
    .eq('key', 'billing_enabled')
    .maybeSingle();

  if (error) {
    console.error('Failed to load billing flag:', error);
    return false;
  }
  billingEnabledCache = data?.value === true;
  return billingEnabledCache;
}

/** Fetch the current user's subscription (cached per session). */
export async function fetchSubscription(force = false): Promise<Subscription | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) return null;

  if (!force && subCache?.userId === user.id) return subCache.sub;

  const { data, error } = await supabase
    .from('subscriptions')
    .select('status, plan_id, current_period_end, cancel_at_period_end, trial_end')
    .eq('user_id', user.id)
    .maybeSingle();

  if (error) {
    console.error('Failed to load subscription:', error);
    return null;
  }

  const sub: Subscription | null = data
    ? {
        status: data.status,
        planId: data.plan_id,
        currentPeriodEnd: data.current_period_end ?? null,
        cancelAtPeriodEnd: data.cancel_at_period_end ?? false,
        trialEnd: data.trial_end ?? null,
      }
    : null;

  subCache = { userId: user.id, sub };
  return sub;
}

/** True when the subscription grants access (active or trialing). */
export function isSubscriptionActive(sub: Subscription | null): boolean {
  return sub?.status === 'active' || sub?.status === 'trialing';
}

export function invalidateSubscriptionCache(): void {
  subCache = null;
  billingEnabledCache = null;
}

async function authedPost(path: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');

  const res = await fetch(`${FUNCTIONS_BASE}/${path}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: '{}',
  });
  const result = await res.json();
  if (!res.ok || !result.url) {
    throw new Error(result.error || 'Request failed');
  }
  return result.url as string;
}

/** Start Stripe Checkout and redirect the browser to it. */
export async function startCheckout(): Promise<void> {
  const url = await authedPost('stripe-checkout');
  window.location.href = url;
}

/** Open the Stripe Billing Portal and redirect the browser to it. */
export async function openBillingPortal(): Promise<void> {
  const url = await authedPost('stripe-portal');
  window.location.href = url;
}

/** Admin only: flip the global billing switch. Enforced by RLS. */
export async function setBillingEnabled(enabled: boolean): Promise<{ error: string | null }> {
  const { data: { session } } = await supabase.auth.getSession();
  const { error } = await supabase
    .from('app_settings')
    .upsert(
      {
        key: 'billing_enabled',
        value: enabled,
        updated_at: new Date().toISOString(),
        updated_by: session?.user?.id ?? null,
      },
      { onConflict: 'key' },
    );
  if (error) return { error: error.message };
  billingEnabledCache = enabled;
  return { error: null };
}
